import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import type { ImportComparisonRow } from "../../lib/provider-import-comparison";

export function ProviderImportComparison({
  rows,
  selectedFieldPaths,
  canSelectFields = false,
  disabled = false,
  onFieldChange,
}: {
  rows: ImportComparisonRow[];
  selectedFieldPaths?: string[];
  canSelectFields?: boolean;
  disabled?: boolean;
  onFieldChange?: (input: { path: string; selected: boolean }) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div
        aria-hidden="true"
        className="hidden grid-cols-2 gap-4 border-b bg-slate-50 px-4 py-3 text-sm font-semibold sm:grid"
      >
        <span>Organization record</span>
        <span>Provider version</span>
      </div>
      <dl className="divide-y divide-slate-100">
        {rows.map((row) => {
          const selected =
            selectedFieldPaths === undefined ||
            selectedFieldPaths.includes(row.path);
          return (
            <div
              key={row.path}
              className={cn(
                "space-y-2 px-4 py-3",
                row.changed && selected && "bg-blue-50/50",
              )}
            >
              <dt className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-700">
                {canSelectFields && row.changed ? (
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={selected}
                      disabled={disabled}
                      aria-label={`Import ${row.label}`}
                      onCheckedChange={(checked) =>
                        onFieldChange?.({
                          path: row.path,
                          selected: checked === true,
                        })
                      }
                    />
                    {row.label}
                  </label>
                ) : (
                  row.label
                )}
                {row.changed && !selected && (
                  <span className="text-slate-500">Not selected</span>
                )}
                {row.cleared && selected && (
                  <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-900">
                    Will be cleared
                  </span>
                )}
                {row.changed && !row.cleared && selected && (
                  <span className="text-blue-700">Changed</span>
                )}
              </dt>
              <dd className="grid gap-3 text-sm sm:grid-cols-2 sm:gap-4">
                <div className="min-w-0 whitespace-pre-wrap break-words text-slate-600">
                  <span className="mb-1 block text-xs text-slate-500 sm:sr-only">
                    Organization record
                  </span>
                  {row.before}
                </div>
                <div className="min-w-0 whitespace-pre-wrap break-words text-slate-900">
                  <span className="mb-1 block text-xs text-slate-500 sm:sr-only">
                    Provider version
                  </span>
                  {row.after}
                </div>
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
